import React, { useEffect, useMemo, useState } from "react";
import { WitterungsPanel } from "./witterungspanel.jsx";
import { flaschenInRegion, flaschenVonWein, rebsortenListe, rebsortenNamen, regionZuWein, winzerZuWein } from "../kern/fachlogik.js";
import { passtWahlMit } from "../kern/filterwahl.js";
import { fmtZahl, sortDe } from "../kern/format.js";
import { usePersistentState } from "../ui/hooks.jsx";
import { Btn, JahrgangsLinks, Leer, LoeschenKnopf, Seitenkopf, TextKnopf, TextLoeschen, TypPunkt, Werkzeugleiste } from "../ui/primitive.jsx";
import { MehrfachAuswahl } from "../ui/mehrfachauswahl.jsx";
import { Begrenzt, KurzListe, kurzText } from "../ui/begrenzt.jsx";
import { LISTEN_GRENZEN as G } from "../kern/konstanten.js";
import { t } from "../i18n/index.js";
import { landName } from "../i18n/laender.js";


/* §6 ─ Ansichten ──────────────────────────────────────────────── */

function RegionKarte({ region }) {
  const d = 0.35;
  const bbox = [region.lon - d, region.lat - d, region.lon + d, region.lat + d]
    .map((x) => x.toFixed(4)).join("%2C");
  const src = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${region.lat}%2C${region.lon}`;
  return (
    <div className="wl-karte">
      <iframe title={`Karte ${region.name}`} src={src} loading="lazy" />
      <span className="wl-kartenlegende num">
        {fmtZahl(region.lat, 3)}° N · {fmtZahl(region.lon, 3)}° O · © OpenStreetMap-Mitwirkende
      </span>
    </div>
  );
}


function RegionenAnsicht({ db, land, nurBestand, setNurBestand, geheZu, oeffneModal, loescheRegion }) {
  const [suche, setSuche] = usePersistentState("regionen.suche", "");
  /* Mehrfachwahl: Liste, leer heißt alle (kern/filterwahl.js). */
  const [rebsorteFilter, setRebsorteFilter] = usePersistentState("regionen.rebsorteFilter", []);

  /* Bei Ebenenwechsel (Land ↔ Übersicht) Suche/Filter zurücksetzen */
  useEffect(() => { setSuche(""); setRebsorteFilter([]); }, [land]);

  /* Erste Ebene: Länder. Zweite Ebene: Regionen des gewählten Landes. */
  const laender = useMemo(() => {
    const map = new Map();
    for (const r of db.regions) {
      const l = r.land || "Ohne Land";
      if (!map.has(l)) map.set(l, []);
      map.get(l).push(r);
    }
    return [...map.entries()]
      .map(([name, regionen]) => ({ name, regionen: regionen.sort(sortDe) }))
      .sort((a, b) => a.name.localeCompare(b.name, "de"));
  }, [db.regions]);

  const q = suche.trim().toLowerCase();

  if (!land) {
    /* Ohne Suche: nach Ländern gruppierte Übersicht. Sobald gesucht wird,
       direkt einzelne Regionen vorschlagen (statt „Land mit Region"). */
    const werkzeug = (
      <Werkzeugleiste
        suche={suche} setSuche={setSuche} sucheLabel={t("Länder & Regionen durchsuchen …")}
        aktion={<Btn variante="voll" onClick={() => oeffneModal({ typ: "region" })}>{t("Region anlegen")}</Btn>}
      />
    );
    if (q) {
      const treffer = [...db.regions]
        .filter((r) => {
          if (nurBestand && flaschenInRegion(db, r.id) === 0) return false;
          const heu = `${r.name} ${r.land || ""} ${r.rebsorten || ""} ${r.beschreibung || ""}`.toLowerCase();
          return heu.includes(q);
        })
        .sort(sortDe);
      return (
        <>
          {werkzeug}
          {treffer.length === 0 ? (
            <Leer text={t("Keine Region gefunden. Passe die Suche an oder lege eine Region an.")} />
          ) : (
            <Begrenzt liste={treffer} grenze={G.regionenKarten} schluessel={q}>{(sichtbarTreffer) => (
            <div className="wl-kartenraster">
              {sichtbarTreffer.map((r) => {
                const winzerZahl = db.winzer.filter((w) => w.regionId === r.id).length;
                const flaschen = flaschenInRegion(db, r.id);
                return (
                  <button className="wl-regionskarte ist-klickbar" key={r.id} onClick={() => geheZu({ view: "region", id: r.id })}>
                    <span className="wl-eyebrow">{r.land ? landName(r.land) : t("Ohne Land")}</span>
                    <span className="wl-regionsname">{r.name}</span>
                    <span className="wl-regionstext">{r.rebsorten || "—"}</span>
                    <span className="wl-regionsfuss">
                      {t.plural(winzerZahl, "ein Winzer", "{n} Winzer")}
                      {" · "}{t.plural(flaschen, "eine Flasche", "{n} Flaschen")}
                    </span>
                  </button>
                );
              })}
            </div>
            )}</Begrenzt>
          )}
        </>
      );
    }
    const sichtbar = laender.filter((l) => !nurBestand || l.regionen.some((r) => flaschenInRegion(db, r.id) > 0));
    return (
      <>
        {werkzeug}
        {sichtbar.length === 0 ? (
          <Leer text={t("Kein Land gefunden. Passe Suche und Filter an oder lege eine Region an.")} />
        ) : (
          <div className="wl-kartenraster">
            {sichtbar.map(({ name, regionen }) => {
              const winzerZahl = db.winzer.filter((w) => regionen.some((r) => r.id === w.regionId)).length;
              const flaschen = regionen.reduce((s, r) => s + flaschenInRegion(db, r.id), 0);
              return (
                <button className="wl-regionskarte ist-klickbar" key={name} onClick={() => geheZu({ view: "regionen", land: name })}>
                  <span className="wl-eyebrow">{t.plural(regionen.length, "eine Region", "{n} Regionen")}</span>
                  <span className="wl-regionsname">{name === "Ohne Land" ? t("Ohne Land") : landName(name)}</span>
                  <span className="wl-regionstext">{kurzText(regionen.map((r) => r.name), G.laenderkarteRegionen.erste)}</span>
                  <span className="wl-regionsfuss">
                    {t.plural(winzerZahl, "ein Winzer", "{n} Winzer")}
                    {" · "}{t.plural(flaschen, "eine Flasche", "{n} Flaschen")}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </>
    );
  }

  const alleRegionenImLand = laender.find((l) => l.name === land)?.regionen ?? [];
  const rebsorten = rebsortenListe(alleRegionenImLand.map((r) => ({ rebsorten: r.rebsorten })));
  const regionen = alleRegionenImLand.filter((r) => {
    if (nurBestand && flaschenInRegion(db, r.id) === 0) return false;
    if (!passtWahlMit(rebsorteFilter, (sorte) =>
      String(r.rebsorten || "").split(",").some((x) => x.trim().toLowerCase() === sorte.toLowerCase()))) return false;
    if (q) {
      const heu = `${r.name} ${r.beschreibung || ""} ${r.rebsorten || ""}`.toLowerCase();
      if (!heu.includes(q)) return false;
    }
    return true;
  });

  /* Gespeichert bleibt der Freitext des Nutzers; angezeigt wird der
     Ländername in der Anzeigesprache. */
  const landAnzeige = land === "Ohne Land" ? t("Ohne Land") : landName(land);

  return (
    <>
      <Seitenkopf
        krume={<><TextKnopf onClick={() => geheZu({ view: "regionen" })}>{t("Regionen")}</TextKnopf><span> / {landAnzeige}</span></>}
        titel={landAnzeige}
        aktionen={<Btn variante="voll" onClick={() => oeffneModal({ typ: "region", kontext: { land } })}>{t("Region anlegen")}</Btn>}
      />
      <Werkzeugleiste
        suche={suche} setSuche={setSuche} sucheLabel={`Regionen in ${landAnzeige} durchsuchen …`}
        filterAktiv={rebsorteFilter.length > 0}
        onReset={() => setRebsorteFilter([])}
        filter={
          <MehrfachAuswahl wahl={rebsorteFilter} setWahl={setRebsorteFilter} alleText={t("Alle Rebsorten")}
            beschriftung={t("Nach Rebsorte filtern")} optionen={rebsorten.map((r) => ({ wert: r, label: r }))} />
        }
      />
      {regionen.length === 0 ? (
        <Leer text={t("Keine Region gefunden — Suche und Filter anpassen oder eine Region anlegen.")}
          aktion={<Btn onClick={() => geheZu({ view: "regionen" })}>{t("Zurück zu den Ländern")}</Btn>} />
      ) : (
        <Begrenzt liste={regionen} grenze={G.regionenKarten} schluessel={`${land}|${q}|${rebsorteFilter}|${nurBestand}`}>{(sichtbareRegionen) => (
        <div className="wl-kartenraster">
          {sichtbareRegionen.map((region) => {
            const winzerZahl = db.winzer.filter((w) => w.regionId === region.id).length;
            const flaschen = flaschenInRegion(db, region.id);
            return (
              <div className="wl-regionskarte" key={region.id}>
                <span className="wl-eyebrow">{landName(region.land)}</span>
                <button className="wl-regionsname ist-link" onClick={() => geheZu({ view: "region", id: region.id })}>{region.name}</button>
                <span className="wl-regionstext">{region.beschreibung}</span>
                <span className="wl-regionsfuss">
                  {region.rebsorten ? region.rebsorten.split(",")[0].trim() + " u. a." : "—"}
                  {" · "}{t.plural(winzerZahl, "ein Winzer", "{n} Winzer")}
                  {" · "}{t.plural(flaschen, "eine Flasche", "{n} Flaschen")}
                </span>
                <span className="wl-kartenaktionen">
                  <TextKnopf onClick={() => geheZu({ view: "region", id: region.id })}>{t("Öffnen")}</TextKnopf>
                  {" "}
                  <TextKnopf onClick={() => oeffneModal({ typ: "region", objekt: region })}>{t("Bearbeiten")}</TextKnopf>
                  {" "}
                  <TextLoeschen
                    disabledGrund={winzerZahl > 0 ? "Zuerst die Winzer dieser Region löschen oder umziehen." : null}
                    onDelete={() => loescheRegion(region.id)} />
                </span>
              </div>
            );
          })}
        </div>
        )}</Begrenzt>
      )}
    </>
  );
}

function RegionDetail({ id, db, setDb, geheZu, oeffneModal, loescheRegion, vorwahlJahr, vorwahlAppellation, darfWetter = false, nurBestand = false }) {
  const region = db.regions.find((r) => r.id === id);
  const [appFilter, setAppFilter] = useState(vorwahlAppellation || "");
  useEffect(() => { if (vorwahlAppellation) setAppFilter(vorwahlAppellation); }, [vorwahlAppellation]);
  /* Von der Schale, nicht aus db.prefs: In der Sicht „Alle Weine“ ist
     der Bestandsfilter gegenstandslos — Katalogobjekte haben nie
     Flaschen. Direkt gelesen blieben Listen dort leer, obwohl Weine
     vorhanden sind. */
  if (!region) return <Leer text={t("Diese Region existiert nicht mehr.")} aktion={<Btn onClick={() => geheZu({ view: "regionen" })}>{t("Zu den Regionen")}</Btn>} />;
  const winzerListe = db.winzer.filter((w) => w.regionId === region.id).sort(sortDe);
  /* Weine dieser Region: alle, deren (ggf. abweichende) Region hier liegt. */
  const alleWeine = db.weine.filter((w) => { const r = regionZuWein(db, w); return r && r.id === region.id; }).sort(sortDe);
  const appellationen = [...new Set(alleWeine.map((w) => (w.appellation || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b, "de"));
  const nachApp = appFilter ? alleWeine.filter((w) => (w.appellation || "").trim() === appFilter) : alleWeine;
  /* Anzeige respektiert zusätzlich „Nur mit Bestand". */
  const weine = nurBestand ? nachApp.filter((w) => flaschenVonWein(w) > 0) : nachApp;
  const flaschen = alleWeine.reduce((sum, w) => sum + flaschenVonWein(w), 0);

  return (
    <>
      <Seitenkopf
        krume={
          <>
            <TextKnopf onClick={() => geheZu({ view: "regionen" })}>{t("Regionen")}</TextKnopf>
            <span> / </span>
            <TextKnopf onClick={() => geheZu({ view: "regionen", land: region.land || "Ohne Land" })}>{region.land ? landName(region.land) : t("Ohne Land")}</TextKnopf>
            <span> / {region.name}</span>
          </>
        }
        titel={region.name}
        unterzeile={landName(region.land)}
        aktionen={
          <>
            {flaschen > 0 && (
              <Btn onClick={() => geheZu({ view: "keller", filter: { regionId: region.id } })}>{t("Im Keller zeigen")}</Btn>
            )}
            <Btn onClick={() => oeffneModal({ typ: "region", objekt: region })}>{t("Bearbeiten")}</Btn>
            <LoeschenKnopf
              label={t("Region löschen")}
              disabledGrund={winzerListe.length > 0 ? "Zuerst die Winzer dieser Region löschen oder umziehen." : null}
              onDelete={() => { loescheRegion(region.id); geheZu({ view: "regionen" }); }}
            />
          </>
        }
      />

      <div className="wl-zweispalt">
        <RegionKarte region={region} />
        <div>
          <p className="wl-fliesstext">{region.beschreibung || "Noch keine Beschreibung hinterlegt."}</p>
          <dl className="wl-fakten">
            <dt>{t("Rebsorten")}</dt><dd>{region.rebsorten || "—"}</dd>
            <dt>{t("Koordinaten")}</dt><dd className="num">{fmtZahl(region.lat, 3)}° N · {fmtZahl(region.lon, 3)}° O</dd>
            <dt>{t("Bestand")}</dt><dd className="num">{flaschen} Flaschen aus {alleWeine.length} {alleWeine.length === 1 ? "Wein" : "Weinen"}</dd>
            {appellationen.length > 0 && <><dt>{t("Appellationen")}</dt><dd>
              <KurzListe grenze={G.regionAppellationen.erste} trenner=" · "
                elemente={appellationen.map((a) => <span key={a}>{a}</span>)} />
            </dd></>}
            <dt>{t("Winzer")}</dt>
            <dd>
              {winzerListe.length === 0 ? t("Noch keine Winzer in dieser Region.") : (
                <KurzListe grenze={G.regionWinzer.erste} trenner=" · "
                  elemente={winzerListe.map((w) => (
                    <TextKnopf key={w.id} onClick={() => geheZu({ view: "winzerDetail", id: w.id })}>{w.name}</TextKnopf>
                  ))} />
              )}
            </dd>
          </dl>
        </div>
      </div>

      <section className="wl-abschnitt">
        <div className="wl-abschnittskopf">
          <h2 className="wl-abschnittstitel">{t("Weine dieser Region")}</h2>
        </div>
        {appellationen.length > 0 && (
          <div className="wl-appchips">
            <button className={"wl-appchip" + (appFilter === "" ? " ist-aktiv" : "")} onClick={() => setAppFilter("")}>{t("Alle")}</button>
            {/* Ein gewählter Chip bleibt auch zugeklappt sichtbar. */}
            <KurzListe grenze={G.regionAppellationen.erste} immer={new Set([appFilter])} className="wl-appchips-rest"
              elemente={appellationen.map((a) => (
                <button key={a} className={"wl-appchip" + (appFilter === a ? " ist-aktiv" : "")} onClick={() => setAppFilter(a)}>{a}</button>
              ))} />
          </div>
        )}
        {alleWeine.length === 0 ? (
          <Leer text={t("Aus dieser Region ist noch kein Wein erfasst.")} />
        ) : weine.length === 0 ? (
          <Leer text={appFilter
            ? (nurBestand ? `Kein Wein mit Appellation „${appFilter}" hat Bestand.` : `Kein Wein mit Appellation „${appFilter}".`)
            : "Kein Wein dieser Region hat aktuell Bestand. Deaktiviere „Nur mit Bestand“, um alle zu sehen."} />
        ) : (
          <Begrenzt liste={weine} grenze={G.regionWeine} schluessel={`${region.id}|${appFilter}|${nurBestand}`}>{(sichtbareWeine) => (
          <div className="wl-scroll">
            <table className="wl-tabelle">
              <thead><tr><th>{t("Wein")}</th><th>{t("Winzer")}</th><th>{t("Appellation")}</th><th>{t("Typ")}</th><th>{t("Rebsorten")}</th><th>{t("Jahrgänge")}</th><th className="num">{t("Flaschen")}</th></tr></thead>
              <tbody>
                {sichtbareWeine.map((wein) => {
                  const winzer = winzerZuWein(db, wein);
                  return (
                    <tr key={wein.id}>
                      <td><button className="wl-zeilentitel" onClick={() => geheZu({ view: "wein", id: wein.id })}>{wein.name}{wein.favorit && <span className="wl-favorit" title={t("Lieblingswein")}>★</span>}</button></td>
                      <td>{winzer ? <TextKnopf onClick={() => geheZu({ view: "winzerDetail", id: winzer.id })}>{winzer.name}</TextKnopf> : "—"}</td>
                      <td>{wein.appellation ? <TextKnopf className="ist-dezent" onClick={() => setAppFilter(wein.appellation)}>{wein.appellation}</TextKnopf> : "—"}</td>
                      <td><TypPunkt typ={wein.typ} /></td>
                      <td>{rebsortenNamen(wein) || "—"}</td>
                      <td><JahrgangsLinks wein={wein} nurBestand={nurBestand} geheZu={geheZu} /></td>
                      <td className="num">{flaschenVonWein(wein)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}</Begrenzt>
        )}
      </section>

      {darfWetter && <WitterungsPanel region={region} db={db} setDb={setDb} vorwahlJahr={vorwahlJahr} />}
    </>
  );
}

export { RegionDetail, RegionKarte, RegionenAnsicht };
